/**
 * Measures: rows a data tile derives from its table. Nets, differences and
 * means combine category rows; rolling averages, change versus the previous
 * column and rank read one row (a category or another measure) along the
 * columns. Values are computed per column in the tile's statistic from the
 * raw model and stay unrounded: the cell format rounds them on display.
 * A difference or delta of percentages is in points, the fractions' gap
 * times 100, as is a net of percentages whose format is "points".
 * Everything here is pure.
 */

import type {
  AxisLeaf,
  ModelCell,
  NumberFormatSpec,
  StatKind,
  TableModel,
} from "utils/tableModel"
import { lettersCellOf, STAT_REGISTRY } from "utils/tableModel"
import type {
  Measure,
  RowsDisplay,
} from "features/Dashboard/types/dashboardDocument"
import { ChartUnit, leafLabel, UNIT_LABEL } from "./modelAdapters"
import { DownRow, downRowsOf, withDownRows } from "./downRows"

export interface MeasureProblem {
  measureId: string
  message: string
}

type Values = (number | null)[]

/** The first number in `label`: "9" gives 9, "5 - Very satisfied" 5, "Don't know" null. */
export const parseScaleValue = (label: string): number | null => {
  const m = /-?\d+(\.\d+)?/.exec(label)
  return m ? Number(m[0]) : null
}

/**
 * Default NPS bands over the categories of `leaves` (each id once, Totals
 * left out): promoters score 9 or 10, detractors 0 to 6. Null when fewer
 * than 7 categories carry a number.
 */
export const defaultNpsBands = (
  leaves: AxisLeaf[]
): { promoters: string[]; detractors: string[] } | null => {
  const seen = new Set<string>()
  const scored: { id: string; v: number }[] = []
  leaves.forEach((l) => {
    if (l.isTotal || seen.has(l.id)) return
    seen.add(l.id)
    const v = parseScaleValue(leafLabel(l))
    if (v !== null) scored.push({ id: l.id, v })
  })
  if (scored.length < 7) return null
  return {
    promoters: scored.filter((s) => s.v === 9 || s.v === 10).map((s) => s.id),
    detractors: scored.filter((s) => s.v >= 0 && s.v <= 6).map((s) => s.id),
  }
}

const numeric = (cell: ModelCell | undefined): number | null =>
  cell && typeof cell.raw === "number" && Number.isFinite(cell.raw)
    ? cell.raw
    : null

/**
 * `statistic` when the table holds it, else the first statistic it holds,
 * as the chart adapters fall back; `statistic` itself when the table holds
 * none.
 */
export const heldStatistic = (model: TableModel, statistic: string): string => {
  const leaves =
    model.statAxis === "down" ? model.down.leaves : model.across.leaves
  if (leaves.some((l) => l.stat === statistic)) return statistic
  return leaves.find((l) => l.stat !== undefined)?.stat ?? statistic
}

/** Whether column `c` holds `statistic` (every column does with stats down). */
const holds = (model: TableModel, c: number, statistic: string): boolean =>
  model.statAxis === "down" || model.across.leaves[c]?.stat === statistic

/** Index of category `id`'s row: its `statistic` row when stats run down. */
const rowIndex = (model: TableModel, id: string, statistic: string): number =>
  model.down.leaves.findIndex(
    (l) => l.id === id && (model.statAxis === "across" || l.stat === statistic)
  )

/**
 * Category `id` per column in `statistic` (null in the columns of other
 * statistics), or null when the table has no such category.
 */
const categoryValues = (
  model: TableModel,
  id: string,
  statistic: string
): Values | null => {
  const r = rowIndex(model, id, statistic)
  if (r < 0) return null
  return model.across.leaves.map((_, c) =>
    holds(model, c, statistic) ? numeric(model.cells[r]?.[c]) : null
  )
}

/** The non-Totals columns holding `statistic`, in table order: the periods. */
const periodColumns = (model: TableModel, statistic: string): number[] =>
  model.across.leaves.flatMap((l, c) =>
    !l.isTotal && holds(model, c, statistic) ? [c] : []
  )

/**
 * The base of each column, as a KPI shows it (see `modelCell`): the Totals
 * row's "n" in that column, or with stats across in the "n" column of the
 * same category. Null where the table has no "n" or no Totals.
 */
export const columnBases = (model: TableModel): (number | null)[] => {
  const { down, across, cells } = model
  if (model.statAxis === "down") {
    const totalsN = down.leaves.findIndex((l) => l.isTotal && l.stat === "n")
    return across.leaves.map((_, c) =>
      totalsN >= 0 ? numeric(cells[totalsN]?.[c]) : null
    )
  }
  const totalsRow = down.leaves.findIndex((l) => l.isTotal)
  return across.leaves.map((leaf) => {
    const nCol = across.leaves.findIndex(
      (l) => l.id === leaf.id && l.stat === "n"
    )
    return totalsRow >= 0 && nCol >= 0
      ? numeric(cells[totalsRow]?.[nCol])
      : null
  })
}

/** Whether `statistic` is a percentage (col%, row%, CRR% and the like). */
export const isPercentStat = (statistic: string): boolean =>
  Object.hasOwn(STAT_REGISTRY, statistic)
    ? STAT_REGISTRY[statistic].kind === "percent"
    : statistic.includes("%")

/**
 * What a measure's values are: a percent for nets of a percentage (points,
 * "index", when the net's format is "points"), a count for nets of counts
 * and for ranks, a mean for means, points ("index") for differences and
 * deltas. A rolling average is what its source is, except that counts
 * average to a mean.
 */
const kindOf = (
  measure: Measure,
  statistic: string,
  byId: Map<string, Measure>,
  depth = 0
): StatKind => {
  switch (measure.kind) {
    case "rank":
      return "count"
    case "mean":
      return "mean"
    case "difference":
    case "delta":
      return "index"
    case "rolling": {
      const source = byId.get(measure.source)
      const kind =
        source && depth < byId.size
          ? kindOf(source, statistic, byId, depth + 1)
          : isPercentStat(statistic)
            ? "percent"
            : "count"
      return kind === "count" ? "mean" : kind
    }
    case "net":
      if (!isPercentStat(statistic)) return "count"
      return measure.format === "points" ? "index" : "percent"
  }
}

/** Whether row or measure `id` holds percentages: a measure by its kind, a row by the statistic. */
const holdsPercent = (
  id: string,
  statistic: string,
  byId: Map<string, Measure>
): boolean => {
  const measure = byId.get(id)
  return measure
    ? kindOf(measure, statistic, byId) === "percent"
    : isPercentStat(statistic)
}

/**
 * 100 for a difference or delta of percentages, which are points
 * (0.088 apart is 8.8 points), and for a net of percentages shown in
 * points (0.35 is 35), else 1. Other measures keep the scale of what they
 * read.
 */
const pointsScale = (
  measure: Measure,
  statistic: string,
  byId: Map<string, Measure>
): number => {
  switch (measure.kind) {
    case "difference":
      return holdsPercent(measure.plus, statistic, byId) &&
        holdsPercent(measure.minus, statistic, byId)
        ? 100
        : 1
    case "delta":
      return holdsPercent(measure.source, statistic, byId) ? 100 : 1
    case "net":
      return measure.format === "points" && isPercentStat(statistic) ? 100 : 1
    default:
      return 1
  }
}

/**
 * One measure per column, in `statistic` (or the first statistic the table
 * holds when it lacks that one). `computed` holds the values of the
 * measures computed before, by id; a source that is neither a computed
 * measure nor a category gives a blank row. Rolling, delta and rank read
 * the periods only, so they leave Totals columns blank. A difference or
 * delta of percentages, and a net of percentages in the "points" format,
 * are in points (times 100); `measures` tells which sources are
 * percentages by their kind, and a source it does not define is a
 * percentage when the statistic is one.
 */
export const measureValues = (
  model: TableModel,
  measure: Measure,
  statistic: string,
  computed: Map<string, Values>,
  measures: Measure[] = []
): Values => {
  const stat = heldStatistic(model, statistic)
  const scale = pointsScale(
    measure,
    stat,
    new Map(measures.map((m) => [m.id, m]))
  )
  const cols = model.across.leaves.length
  const blank = (): Values => Array.from({ length: cols }, () => null)
  const sourceOf = (id: string): Values | null =>
    computed.get(id) ?? categoryValues(model, id, stat)
  switch (measure.kind) {
    case "net": {
      const rows = measure.members
        .map((m) => categoryValues(model, m, stat))
        .filter((v): v is Values => v !== null)
      if (rows.length === 0) return blank()
      return Array.from({ length: cols }, (_, c) => {
        const sum = rows.reduce<number | null>((acc, row) => {
          const v = row[c]
          return v === null ? acc : (acc ?? 0) + v
        }, null)
        return sum === null ? null : sum * scale
      })
    }
    case "difference": {
      const a = sourceOf(measure.plus)
      const b = sourceOf(measure.minus)
      if (!a || !b) return blank()
      return a.map((v, c) => {
        const w = b[c]
        return v === null || w === null ? null : (v - w) * scale
      })
    }
    case "mean": {
      const rows = Object.entries(measure.scaleValues).flatMap(
        ([id, value]) => {
          const row = categoryValues(model, id, stat)
          return row ? [{ value, row }] : []
        }
      )
      if (rows.length === 0) return blank()
      return Array.from({ length: cols }, (_, c) => {
        let num = 0
        let den = 0
        rows.forEach(({ value, row }) => {
          const v = row[c]
          if (v !== null) {
            num += value * v
            den += v
          }
        })
        return den > 0 ? num / den : null
      })
    }
    case "rolling": {
      const src = sourceOf(measure.source)
      if (!src) return blank()
      const bases = columnBases(model)
      const order = periodColumns(model, stat)
      const out = blank()
      order.forEach((c, pos) => {
        if (measure.requireFullWindow && pos + 1 < measure.periods) return
        const window = order.slice(
          Math.max(0, pos - measure.periods + 1),
          pos + 1
        )
        let num = 0
        let den = 0
        window.forEach((wc) => {
          const v = src[wc]
          const base = bases[wc] ?? 1
          if (v !== null) {
            num += v * base
            den += base
          }
        })
        out[c] = den > 0 ? num / den : null
      })
      return out
    }
    case "delta": {
      const src = sourceOf(measure.source)
      if (!src) return blank()
      const order = periodColumns(model, stat)
      const out = blank()
      order.forEach((c, pos) => {
        if (pos === 0) return
        const prev = src[order[pos - 1]]
        const cur = src[c]
        out[c] = prev === null || cur === null ? null : (cur - prev) * scale
      })
      return out
    }
    case "rank": {
      const src = sourceOf(measure.source)
      if (!src) return blank()
      const sign = measure.direction === "desc" ? -1 : 1
      const ranked = periodColumns(model, stat)
        .flatMap((c) => {
          const v = src[c]
          return v === null ? [] : [{ c, v }]
        })
        .sort((a, b) => sign * (a.v - b.v))
      const out = blank()
      // Ties share a rank; the next value skips the places they took.
      let rank = 0
      ranked.forEach(({ c, v }, i) => {
        if (i === 0 || v !== ranked[i - 1].v) rank = i + 1
        out[c] = rank
      })
      return out
    }
  }
}

/** The rows and measures `measure` reads by id. */
const dependsOn = (measure: Measure): string[] => {
  switch (measure.kind) {
    case "difference":
      return [measure.plus, measure.minus]
    case "rolling":
    case "delta":
    case "rank":
      return [measure.source]
    default:
      return []
  }
}

/**
 * Every measure's values by id, each computed after the measures it reads
 * whatever their order in the list. In a cycle, the measure computed first
 * reads the other as missing.
 */
const computeAll = (
  model: TableModel,
  measures: Measure[],
  statistic: string
): Map<string, Values> => {
  const byId = new Map(measures.map((m) => [m.id, m]))
  const computed = new Map<string, Values>()
  const visiting = new Set<string>()
  const visit = (m: Measure): void => {
    if (computed.has(m.id) || visiting.has(m.id)) return
    visiting.add(m.id)
    dependsOn(m).forEach((id) => {
      const dep = byId.get(id)
      if (dep) visit(dep)
    })
    computed.set(m.id, measureValues(model, m, statistic, computed, measures))
  }
  measures.forEach(visit)
  return computed
}

/**
 * Every measure's values per column, by id, in `statistic` (or the first
 * statistic the table holds when it lacks that one): `measureValues` for
 * each, with the whole list at hand, so a difference or change of a measure
 * knows whether that measure holds percentages. Measures that read another
 * are computed after it.
 */
export const measureMap = (
  model: TableModel,
  measures: Measure[],
  statistic: string
): Map<string, Values> =>
  computeAll(model, measures, heldStatistic(model, statistic))

const DEFAULT_DECIMALS: Partial<Record<StatKind, number>> = {
  percent: 1,
  mean: 2,
  count: 0,
  index: 1,
}

/**
 * The unit of a measure of `kind`: a rank is a rank, the others follow
 * their kind (differences, deltas and nets in the "points" format are
 * points, index figures).
 */
const measureUnit = (measure: Measure, kind: StatKind): ChartUnit => {
  if (measure.kind === "rank") return "rank"
  switch (kind) {
    case "percent":
      return "percent"
    case "index":
      return "points"
    case "mean":
      return "mean"
    default:
      return "count"
  }
}

/**
 * Whether `measure` is in points of percentages: scaled by 100 (see
 * `pointsScale`), or a rolling average of a measure that is.
 */
const inPoints = (
  measure: Measure,
  statistic: string,
  byId: Map<string, Measure>,
  depth = 0
): boolean => {
  if (pointsScale(measure, statistic, byId) !== 1) return true
  if (measure.kind !== "rolling" || depth >= byId.size) return false
  const source = byId.get(measure.source)
  return source !== undefined && inPoints(source, statistic, byId, depth + 1)
}

/**
 * The measure's own decimals and format over the defaults of its kind;
 * points of percentages never need a thousands separator.
 */
const formatOf = (
  measure: Measure,
  kind: StatKind,
  points: boolean
): NumberFormatSpec => ({
  decimals: measure.decimals ?? DEFAULT_DECIMALS[kind] ?? 1,
  thousands: kind !== "percent" && !points,
  percent:
    kind === "percent" &&
    measure.format !== "points" &&
    measure.format !== "number",
})

/**
 * What stops a measure from computing fully: members or scored rows the
 * table lacks, a row or measure it reads that is missing, rolling or
 * change versus previous on a table without periods (fewer than two
 * non-Totals columns), a rolling average on a table without a base ("n"),
 * which it can only average unweighted, and a measure that reads itself
 * through the measures it reads (a hand-edited file can hold one).
 */
export const measureProblems = (
  model: TableModel,
  measures: Measure[]
): MeasureProblem[] => {
  const rows = new Set(
    model.down.leaves.filter((l) => !l.isTotal).map((l) => l.id)
  )
  const ids = new Set(measures.map((m) => m.id))
  const known = (id: string): boolean => rows.has(id) || ids.has(id)
  const periods = new Set(
    model.across.leaves.filter((l) => !l.isTotal).map((l) => l.id)
  ).size
  const unweighted = columnBases(model).every((b) => b === null)
  const problems: MeasureProblem[] = []
  const report = (m: Measure, message: string): void => {
    problems.push({ measureId: m.id, message })
  }
  const absent = (m: Measure, members: string[]): void => {
    const gone = members.filter((x) => !rows.has(x))
    if (gone.length > 0) report(m, `Not in the data: ${gone.join(", ")}`)
  }
  measures.forEach((m) => {
    switch (m.kind) {
      case "net":
        absent(m, m.members)
        break
      case "mean":
        absent(m, Object.keys(m.scaleValues))
        break
      case "difference":
        if (!known(m.plus) || !known(m.minus)) {
          report(m, "A net it depends on is missing")
        }
        break
      case "rolling":
      case "delta":
        if (periods < 2) report(m, "Needs a column variable with periods")
        if (!known(m.source)) report(m, "Its source is missing")
        if (m.kind === "rolling" && unweighted) {
          report(m, "No base column, the rolling average is unweighted")
        }
        break
      case "rank":
        if (!known(m.source)) report(m, "Its source is missing")
        break
    }
  })
  // A measure that reads itself, directly or through other measures, can
  // only read the others as missing (see `computeAll`).
  const byId = new Map(measures.map((m) => [m.id, m]))
  const reaches = (from: string, target: string, seen: Set<string>): boolean => {
    if (from === target) return true
    if (seen.has(from)) return false
    seen.add(from)
    const m = byId.get(from)
    return m
      ? dependsOn(m).some((d) => byId.has(d) && reaches(d, target, seen))
      : false
  }
  measures.forEach((m) => {
    const next = dependsOn(m).find(
      (d) => byId.has(d) && reaches(d, m.id, new Set())
    )
    if (next === undefined) return
    report(
      m,
      next === m.id
        ? `Circular reference: ${m.label} reads itself`
        : `Circular reference between ${m.label} and ${byId.get(next)?.label ?? next}`
    )
  })
  return problems
}

/**
 * The category rows shown beside measures. Stats down: the rows of
 * `statistic`, Totals included, and the letters rows that print their
 * significance. Stats across: every row.
 */
const categoryRows = (model: TableModel, statistic: string): DownRow[] => {
  const all = downRowsOf(model)
  if (model.statAxis === "across") return all
  const kept = new Set<number>()
  all.forEach((row, r) => {
    if (row.leaf.stat === statistic) kept.add(r)
  })
  const letters = new Set<number>()
  kept.forEach((r) =>
    model.cells[r]?.forEach((cell, c) => {
      if (!cell.sig) return
      const at = lettersCellOf(model, r, c)
      if (at) letters.add(at.row)
    })
  )
  return all.filter((_, r) => kept.has(r) || letters.has(r))
}

/**
 * The model a tile shows for `rows`: its categories as they are
 * ("categories", or any tile without measures), its measures only
 * ("measures") or the categories in `statistic` followed by the measures
 * ("both"); with `everyStatistic` (a table), "both" keeps the categories'
 * rows of every statistic. A measure row is a leaf
 * `{ id: measure.id, path: [label] }` (carrying `statistic` when stats run
 * down) whose cells hold the measure's values under the columns of
 * `statistic`, blank elsewhere, formatted by its kind: measure rows are in
 * one statistic whatever the categories show. The leaf's `statLabel` is
 * its unit (`UNIT_LABEL`: "%", "pts", "mean", "rank", or "" for counts),
 * shown in the Statistic column and read by the charts. Measure cells
 * never carry significance.
 */
export const applyMeasures = (
  model: TableModel,
  measures: Measure[],
  rows: RowsDisplay,
  statistic: string,
  opts: { everyStatistic?: boolean } = {}
): TableModel => {
  if (measures.length === 0 || rows === "categories") return model
  const stat = heldStatistic(model, statistic)
  const computed = computeAll(model, measures, stat)
  const byId = new Map(measures.map((m) => [m.id, m]))
  // Each measure row is an element of its own, so row bands and headers
  // never merge it with a neighbour.
  const firstElement =
    Math.max(
      model.down.leaves.length - 1,
      ...model.down.leaves.map((l) => l.elementIndex ?? -1)
    ) + 1
  const measureRows: DownRow[] = measures.map((m, k) => {
    const kind = kindOf(m, stat, byId)
    const format = formatOf(m, kind, inPoints(m, stat, byId))
    const values = computed.get(m.id) ?? []
    return {
      leaf: {
        id: m.id,
        path: [m.label],
        isTotal: false,
        elementIndex: firstElement + k,
        ...(model.statAxis === "down" ? { stat } : {}),
        statLabel: UNIT_LABEL[measureUnit(m, kind)],
      },
      cells: model.across.leaves.map((_, c) => ({
        raw: holds(model, c, stat) ? (values[c] ?? null) : null,
        stat,
        kind,
        format,
      })),
    }
  })
  const categories =
    rows !== "both"
      ? []
      : opts.everyStatistic
        ? downRowsOf(model)
        : categoryRows(model, stat)
  return withDownRows(model, [...categories, ...measureRows])
}
